import { t } from '../../../useTranslations';
import { DEFAULT_RISK_LEVELS } from '../utils/riskLevelDefaults';
import { calculateWeightedRisk, calculateHybridRisk, calculateInvertedRisk } from './riskCalculators';

export class RiskModel {
  constructor() {
    this.indices = {
      boolean: [], // FATF, EU listor etc
      score: [],   // CPI, Basel AML Index etc
    };
    this.version = '1.0';
    this.lastUpdated = new Date();
    this.rules = [];
    this.thresholds = {};
    this.modelType = 'deterministic'; // deterministic, weighted, hybrid, inverted
    this.weights = {}; // För viktad modell
    this.lowestRiskLevel = 'Low'; // Low, Medium
    this.ruleGroups = []; // Rule Groups för boost-beräkning
    this.riskThresholds = {}; // Risk thresholds för varje index
    this.baseRisk = 1; // Grundrisk

    // Konfigurerbara risknivåer med tröskelvärden (0-4 scale)
    this.riskLevels = { ...DEFAULT_RISK_LEVELS };

    // Inverted model configuration
    this.invertedConfig = {
      startingScore: 100,
      booleanDeduction: 20, // Per boolean index när false/0
      maxScoringDeduction: 30, // Max deduction per scoring index
      minimumScore: 0 // Lägsta möjliga score
    };
  }

  validateIndex(indexData) {
    if (!indexData || !indexData.id || !indexData.name) {
      throw new Error('Invalid index data: id and name are required');
    }

    if (!['BOOLEAN', 'SCORING'].includes(indexData.config?.type)) {
      throw new Error('Invalid index type: must be BOOLEAN or SCORING');
    }

    return true;
  }

  // Deterministisk modell - MAX logik: grundrisknivå + risk boost per index, ta MAX
  calculateDeterministicRisk(countryData, indices) {
    let baseRiskValue = this.riskLevels[this.lowestRiskLevel]?.numericValue || 0;
    let maxRiskValue = baseRiskValue;
    let triggeredIndexes = [];
    let anyTriggered = false;

    for (const index of indices) {
      const value = countryData[index.id];
      if (value === undefined || value === null) continue;

      const indexThresholds = this.riskThresholds[index.id] || {};

      const triggerThreshold = indexThresholds.triggerThreshold !== undefined ?
        indexThresholds.triggerThreshold :
        (index.config.type === 'SCORING' ? 70 : 0);

      const riskBoost = indexThresholds.riskBoost !== undefined ?
        indexThresholds.riskBoost :
        (index.config.type === 'BOOLEAN' ? 1 : 1);

      let indexTriggered = false;

      if (index.config.type === 'BOOLEAN') {
        if (value === true || value === 1) {
          indexTriggered = true;
        }
      } else if (index.config.type === 'SCORING') {
        const score = Number(value);
        if (score > triggerThreshold) {
          indexTriggered = true;
        }
      }

      if (indexTriggered) {
        const indexRiskValue = baseRiskValue + riskBoost;

        triggeredIndexes.push({
          name: index.name,
          riskValue: indexRiskValue,
          boost: riskBoost
        });

        maxRiskValue = Math.max(maxRiskValue, indexRiskValue);
        anyTriggered = true;
      }
    }

    const drivingIndexes = triggeredIndexes
      .filter(idx => idx.riskValue === maxRiskValue)
      .map(idx => idx.name);

    const finalLevel = this.numericValueToRiskLevel(maxRiskValue);

    return {
      level: finalLevel,
      details: anyTriggered && maxRiskValue > baseRiskValue ? {
        drivingIndexes: drivingIndexes,
        message: drivingIndexes.length > 1 ?
          `Risk derived from ${drivingIndexes.join(' and ')}` :
          `Risk derived from ${drivingIndexes[0]}`,
        baseRisk: baseRiskValue,
        maxBoost: maxRiskValue - baseRiskValue,
        finalRisk: maxRiskValue
      } : undefined
    };
  }

  // Konvertera numeriskt värde till risknivå
  numericValueToRiskLevel(numericValue) {
    const maxValue = Math.max(...Object.values(this.riskLevels).map(level => level.numericValue));
    const clampedValue = Math.min(numericValue, maxValue);

    for (const [levelName, config] of Object.entries(this.riskLevels)) {
      if (config.numericValue === clampedValue) {
        return levelName;
      }
    }

    let closestLevel = this.lowestRiskLevel;
    let closestDiff = Infinity;

    for (const [levelName, config] of Object.entries(this.riskLevels)) {
      const diff = Math.abs(config.numericValue - clampedValue);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestLevel = levelName;
      }
    }

    return closestLevel;
  }

  // Hjälpfunktion för att jämföra risknivåer
  isHigherRisk(level1, level2) {
    const levels = ['Low', 'Medium', 'High', 'Very High', 'Unacceptable'];
    return levels.indexOf(level1) > levels.indexOf(level2);
  }

  // Delegera till riskCalculators.js
  calculateWeightedRisk(countryData, indices, weights) {
    return calculateWeightedRisk(this, countryData, indices, weights);
  }

  calculateHybridRisk(countryData, indices, weights) {
    return calculateHybridRisk(this, countryData, indices, weights);
  }

  calculateInvertedRisk(countryData, indices, weights) {
    return calculateInvertedRisk(this, countryData, indices, weights);
  }

  // Hjälpmetod för att få risknivå baserat på score
  getRiskLevel(score) {
    if (score >= 75) return 'High';
    if (score >= 50) return 'Medium';
    return this.lowestRiskLevel;
  }

  // Ny huvudberäkningsmetod som använder Rule Groups
  calculateRisk(countryData, indices, weights = {}) {
    if (!countryData || !indices || indices.length === 0) {
      const baseRiskValue = this.riskLevels[this.lowestRiskLevel]?.numericValue || 0;
      return { level: this.numericValueToRiskLevel(baseRiskValue), score: baseRiskValue };
    }

    switch (this.modelType) {
      case 'deterministic':
        return this.calculateDeterministicRisk(countryData, indices);

      case 'weighted':
        return this.calculateWeightedRisk(countryData, indices, weights);

      case 'rule-groups':
        return this.calculateRuleGroupsRisk(countryData, indices);

      case 'inverted':
        return this.calculateInvertedRisk(countryData, indices, weights);

      default:
        throw new Error(`Unknown model type: ${this.modelType}`);
    }
  }

  // Ny Rule Groups beräkningsmetod
  calculateRuleGroupsRisk(countryData, indices) {
    const baseRiskValue = this.riskLevels[this.lowestRiskLevel]?.numericValue || 0;
    let totalRisk = baseRiskValue;
    const triggeredIndexes = [];

    for (const group of this.ruleGroups) {
      let groupBoost = 0;
      const groupTriggers = [];

      for (const indexId of group.indexes || []) {
        const index = indices.find(idx => idx.id === indexId);
        if (!index) continue;

        const boost = this.checkIndexTrigger(countryData, index);
        if (boost > 0) {
          groupBoost = Math.max(groupBoost, boost);
          groupTriggers.push({ indexId, indexName: index.name, boost });
        }
      }

      if (groupBoost > 0) {
        const actualBoost = Math.min(groupBoost, group.maxBoost);
        totalRisk += actualBoost;
        triggeredIndexes.push({
          group: group.name,
          boost: actualBoost,
          maxBoost: group.maxBoost,
          triggers: groupTriggers
        });
      }
    }

    return {
      level: this.numericValueToRiskLevel(totalRisk),
      score: totalRisk,
      details: {
        baseRisk: baseRiskValue,
        totalBoost: totalRisk - baseRiskValue,
        triggeredGroups: triggeredIndexes
      }
    };
  }

  // Kontrollera om ett index triggrar risk boost.
  // Boolean indexes use sensible defaults (trigger on true/1, boost 1) when
  // no explicit riskThresholds are configured — this is the common case for
  // rule-groups where per-index thresholds are not exposed in the UI.
  checkIndexTrigger(countryData, index) {
    const indexThresholds = this.riskThresholds[index.id];
    const value = countryData[index.id];

    if (index.config.type === 'BOOLEAN') {
      const triggerCondition = indexThresholds?.triggerCondition || 'present';
      const riskBoost = indexThresholds?.riskBoost || 1;

      const shouldTrigger = triggerCondition === 'present'
        ? (value === true || value === 1)
        : (value === false || value === 0 || value === null || value === undefined);

      return shouldTrigger ? riskBoost : 0;
    }

    // Scoring indexes require explicit thresholds — no sensible default exists
    if (!indexThresholds) return 0;

    if (index.config.type === 'SCORING' && value !== null && value !== undefined) {
      const numValue = Number(value);
      const triggerThreshold = indexThresholds.triggerThreshold;
      const isInverted = index.config.scale?.isInverted;

      const triggered = isInverted
        ? numValue < triggerThreshold
        : numValue > triggerThreshold;

      if (triggered) {
        return indexThresholds.riskBoost;
      }
    }

    return 0;
  }

  // Mappa numeriskt score till risknivå baserat på konfigurerbara tröskelvärden
  mapScoreToRiskLevel(score) {
    const allowedLevels = this.getAllowedRiskLevels();

    for (const [levelName, config] of Object.entries(allowedLevels)) {
      if (score >= config.min && score < config.max) {
        return levelName;
      }
      if (score >= config.max && config.max === 100) {
        return levelName;
      }
    }
    return this.lowestRiskLevel;
  }

  // Få tillåtna riskklasser baserat på lowestRiskLevel
  getAllowedRiskLevels() {
    const lowestValue = this.riskLevels[this.lowestRiskLevel]?.numericValue || 0;
    const allowedLevels = {};

    for (const [levelName, config] of Object.entries(this.riskLevels)) {
      if (config.numericValue >= lowestValue) {
        allowedLevels[levelName] = config;
      }
    }

    return allowedLevels;
  }

  // Batch-beräkning för alla länder
  calculateBatchRisk(countriesData, indices, weights = {}) {
    const results = {};

    for (const [countryCode, countryData] of Object.entries(countriesData)) {
      try {
        results[countryCode] = this.calculateRisk(countryData, indices, weights);
      } catch (error) {
        console.error(`Error calculating risk for ${countryCode}:`, error);
        results[countryCode] = { level: t('common.unknown'), score: 0, error: error.message };
      }
    }

    return results;
  }

  // Exportera resultat till CSV-format
  exportToCSV(results) {
    const headers = 'Country Code,Risk Level,Risk Score\n';
    const rows = Object.entries(results)
      .map(([countryCode, result]) => `${countryCode},${result.level},${result.score.toFixed(2)}`)
      .join('\n');

    return headers + rows;
  }

  // Konfiguration av modell
  configure(config) {
    this.modelType = config.modelType || this.modelType;
    this.weights = config.weights || this.weights;
    this.lowestRiskLevel = config.lowestRiskLevel || this.lowestRiskLevel;
    this.hybridSettings = config.hybridSettings || this.hybridSettings;
    this.ruleGroups = config.ruleGroups || this.ruleGroups;
    this.riskThresholds = config.riskThresholds || this.riskThresholds;
    this.baseRisk = config.baseRisk || this.baseRisk;
    if (config.riskLevels && Object.keys(config.riskLevels).length > 0) this.riskLevels = config.riskLevels;
    if (config.invertedConfig) this.invertedConfig = { ...this.invertedConfig, ...config.invertedConfig };
    this.lastUpdated = new Date();
  }

  // Mappa score till risknivå (legacy helper for rule groups)
  mapScoreToLevel(score) {
    if (score >= 4) return 'Unacceptable';
    if (score >= 3) return 'Very High';
    if (score >= 2) return 'High';
    if (score >= 1) return 'Medium';
    return 'Low';
  }
}
